/**
 * Org chart for the Team Roster board (Oct 27, 2026).
 *
 * Greg: "can we add an organizational chart function to team roster". His
 * picks: one full tree from him down; sales lines come from the fields that
 * drive pay (RM → team_lead_id → asm_id), everyone else from the display-only
 * team_roster.reports_to_id (migration 20261027i). The chart never moves
 * anyone: tapping a card opens the roster editor.
 *
 * Where a person sits (chartParentCandidates, first ACTIVE one wins):
 *   restore_manager → team lead, then ASM, then "reports to"
 *   team_lead       → ASM, then "reports to"
 *   everyone else   → "reports to"
 * A candidate who is inactive or missing is skipped with a warning on the card,
 * so a stale line shows instead of silently hiding.
 */

export const ROLE_LABEL: Record<string, string> = {
  admin: "Admin",
  sales_director: "Sales Director",
  director_of_operations: "Director of Operations",
  roof_director: "Roof Director",
  asm: "ASM",
  team_lead: "Team Lead",
  restore_manager: "Restore Manager",
  roof_team_manager: "Roof Team Manager",
  roof_specialist: "Roof Specialist",
  report_specialist: "Report Specialist",
  drone_pilot: "Drone Pilot",
  qc: "QC",
  finance: "Finance",
  office: "Office",
  project_manager: "Project Manager",
  canvasser: "Canvasser",
};

export const ROLE_ORDER = [
  "admin", "sales_director", "director_of_operations", "roof_director",
  "asm", "team_lead", "restore_manager",
  "roof_team_manager", "roof_specialist", "report_specialist", "drone_pilot",
  "qc", "finance", "office", "project_manager", "canvasser",
];

/** Roles placed on the chart by team_lead_id / asm_id (the pay fields). */
export const LINE_BY_PAY_ROLES = new Set(["restore_manager", "team_lead"]);

export interface OrgMember {
  id: string;
  full_name: string;
  role: string;
  secondary_role?: string | null;
  team_lead_id: string | null;
  asm_id: string | null;
  reports_to_id: string | null;
  is_active: boolean;
  on_leave?: boolean | null;
}

export interface OrgNode {
  member: OrgMember;
  children: OrgNode[];
  /** Everyone below this person, at any depth. */
  size: number;
  warnings: string[];
}

export interface OrgTree {
  /** The main chart: the root with the most people under it (Greg). */
  top: OrgNode | null;
  /** Everyone else with no one above them, each with whatever sits under them. */
  notPlaced: Array<{ node: OrgNode; reason: string }>;
  /** Active members on the chart, top + not placed. */
  count: number;
}

function roleRank(role: string): number {
  const i = ROLE_ORDER.indexOf(role);
  return i === -1 ? ROLE_ORDER.length : i;
}

export function firstName(full: string): string {
  return full.trim().split(/\s+/)[0] ?? full;
}

export function chartParentCandidates(m: OrgMember): Array<{ id: string | null; label: string }> {
  if (m.role === "restore_manager") {
    return [
      { id: m.team_lead_id, label: "team lead" },
      { id: m.asm_id, label: "ASM" },
      { id: m.reports_to_id, label: "reports to" },
    ];
  }
  if (m.role === "team_lead") {
    return [
      { id: m.asm_id, label: "ASM" },
      { id: m.reports_to_id, label: "reports to" },
    ];
  }
  return [{ id: m.reports_to_id, label: "reports to" }];
}

/** The person this member sits under on the chart, plus any skipped lines. */
export function chartParent(
  m: OrgMember,
  byId: Map<string, OrgMember>,
): { parentId: string | null; warnings: string[] } {
  const warnings: string[] = [];
  for (const c of chartParentCandidates(m)) {
    if (!c.id || c.id === m.id) continue;
    const p = byId.get(c.id);
    if (!p) {
      warnings.push(`${c.label} isn't on the roster`);
      continue;
    }
    if (!p.is_active) {
      warnings.push(`${c.label} ${firstName(p.full_name)} is inactive`);
      continue;
    }
    return { parentId: p.id, warnings };
  }
  return { parentId: null, warnings };
}

function compareMembers(a: OrgMember, b: OrgMember): number {
  return roleRank(a.role) - roleRank(b.role) || a.full_name.localeCompare(b.full_name);
}

export function buildOrgTree(rows: OrgMember[]): OrgTree {
  const active = rows.filter((r) => r.is_active);
  const byId = new Map(rows.map((r) => [r.id, r]));

  const parent = new Map<string, string | null>();
  const warnings = new Map<string, string[]>();
  for (const m of active) {
    const p = chartParent(m, byId);
    parent.set(m.id, p.parentId);
    warnings.set(m.id, [...p.warnings]);
  }

  // Break loops: walk up from each member; a member seen twice on one walk is
  // in a loop. Cut the loop at its alphabetically-first member so the result
  // is the same on every load, and say so on that card.
  const done = new Set<string>();
  for (const m of [...active].sort(compareMembers)) {
    const path: string[] = [];
    const onPath = new Set<string>();
    let cur: string | null = m.id;
    while (cur && !done.has(cur) && !onPath.has(cur)) {
      path.push(cur);
      onPath.add(cur);
      cur = parent.get(cur) ?? null;
    }
    if (cur && onPath.has(cur)) {
      const loop = path.slice(path.indexOf(cur));
      const cut = loop
        .map((id) => byId.get(id)!)
        .sort((a, b) => a.full_name.localeCompare(b.full_name))[0];
      parent.set(cut.id, null);
      warnings.get(cut.id)!.push("reporting lines loop back to them");
    }
    for (const id of path) done.add(id);
  }

  const nodes = new Map<string, OrgNode>();
  for (const m of active) {
    nodes.set(m.id, { member: m, children: [], size: 0, warnings: warnings.get(m.id) ?? [] });
  }
  const roots: OrgNode[] = [];
  for (const m of active) {
    const pid = parent.get(m.id);
    const node = nodes.get(m.id)!;
    if (pid && nodes.has(pid)) nodes.get(pid)!.children.push(node);
    else roots.push(node);
  }

  const finish = (n: OrgNode): number => {
    n.children.sort((a, b) => compareMembers(a.member, b.member));
    n.size = n.children.reduce((s, c) => s + 1 + finish(c), 0);
    return n.size;
  };
  for (const r of roots) finish(r);

  roots.sort((a, b) => b.size - a.size || compareMembers(a.member, b.member));
  const top = roots.length && roots[0].size > 0 ? roots[0] : null;
  const notPlaced = roots
    .filter((r) => r !== top)
    .map((node) => ({ node, reason: notPlacedReason(node) }));

  return { top, notPlaced, count: active.length };
}

function notPlacedReason(node: OrgNode): string {
  if (node.warnings.length) return node.warnings[node.warnings.length - 1];
  return LINE_BY_PAY_ROLES.has(node.member.role) ? "no team lead or ASM set" : "no \"Reports to\" set";
}

/** Find a member's node anywhere in the tree. */
export function findNode(tree: OrgTree, id: string): OrgNode | null {
  const walk = (n: OrgNode): OrgNode | null => {
    if (n.member.id === id) return n;
    for (const c of n.children) {
      const f = walk(c);
      if (f) return f;
    }
    return null;
  };
  const starts = [...(tree.top ? [tree.top] : []), ...tree.notPlaced.map((p) => p.node)];
  for (const s of starts) {
    const f = walk(s);
    if (f) return f;
  }
  return null;
}

/** Everyone under this member on the chart (they can't become this member's boss). */
export function descendantIds(tree: OrgTree, id: string): Set<string> {
  const out = new Set<string>();
  const node = findNode(tree, id);
  const walk = (n: OrgNode) => {
    for (const c of n.children) {
      out.add(c.member.id);
      walk(c);
    }
  };
  if (node) walk(node);
  return out;
}

/** The boss most active members in this role report to, for a new hire. */
export function suggestReportsTo(role: string, rows: OrgMember[]): string | null {
  const counts = new Map<string, number>();
  for (const r of rows) {
    if (!r.is_active || r.role !== role || !r.reports_to_id) continue;
    counts.set(r.reports_to_id, (counts.get(r.reports_to_id) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestN = 0;
  for (const [id, n] of counts) {
    if (n > bestN) {
      best = id;
      bestN = n;
    }
  }
  return best;
}

/**
 * Starting lines for a new hire: the single active team lead / ASM for sales
 * (standing rule since Aug 21 and Sep 15: James is TL for everyone, Alex is
 * the ASM), and the usual boss for their role otherwise.
 */
export function suggestedLines(
  role: string,
  rows: OrgMember[],
): { team_lead_id: string | null; asm_id: string | null; reports_to_id: string | null } {
  const tls = rows.filter((r) => r.is_active && r.role === "team_lead");
  const asms = rows.filter((r) => r.is_active && r.role === "asm");
  const oneTl = tls.length === 1 ? tls[0].id : null;
  const oneAsm = asms.length === 1 ? asms[0].id : null;
  return {
    team_lead_id: role === "restore_manager" ? oneTl : null,
    asm_id: LINE_BY_PAY_ROLES.has(role) ? oneAsm : null,
    reports_to_id: LINE_BY_PAY_ROLES.has(role) ? null : suggestReportsTo(role, rows),
  };
}
